import { z } from 'zod'

// postgres.js returns timestamptz as Date objects; normalise to ISO string at the boundary
const DateString = z.union([z.string(), z.date().transform((d) => d.toISOString())])

export const RatingSchema = z.object({
  source: z.enum(['Internet Movie Database', 'Metacritic', 'Rotten Tomatoes']),
  value: z.string(),
})

export type Rating = Readonly<z.infer<typeof RatingSchema>>

const TitleBaseSchema = z.object({
  id: z.number().int(),
  imdbId: z.string(),
  title: z.string(),
  year: z.number().int().nullable(),
  rated: z.string().nullable(),
  released: z.string().nullable(),
  runtime: z.string().nullable(),
  genre: z.string().nullable(),
  director: z.string().nullable(),
  writer: z.string().nullable(),
  actors: z.string().nullable(),
  plot: z.string().nullable(),
  language: z.string().nullable(),
  country: z.string().nullable(),
  awards: z.string().nullable(),
  poster: z.string().nullable(),
  ratings: z.array(RatingSchema),
  metascore: z.number().nullable(),
  imdbRating: z.number().nullable(),
  imdbVotes: z.number().int().nullable(),
  outdated: z.boolean(),
  mismatched: z.boolean(),
  mismatchCount: z.number().int(),
  mismatchReported: z.boolean(),
  createdAt: DateString,
  updatedAt: DateString,
})

export const FilmSchema = TitleBaseSchema.extend({
  dvd: z.string().nullable(),
  boxOffice: z.string().nullable(),
})

export type Film = Readonly<Omit<z.infer<typeof FilmSchema>, 'ratings'>> & { readonly ratings: ReadonlyArray<Rating> }

export const SeriesSchema = TitleBaseSchema.extend({
  totalSeasons: z.number().int().nullable(),
})

export type Series = Readonly<Omit<z.infer<typeof SeriesSchema>, 'ratings'>> & {
  readonly ratings: ReadonlyArray<Rating>
}

export const TitleSchema = z.discriminatedUnion('kind', [
  FilmSchema.extend({ kind: z.literal('film') }),
  SeriesSchema.extend({ kind: z.literal('series') }),
])

export type Title = (Film & { readonly kind: 'film' }) | (Series & { readonly kind: 'series' })

export const RequestedSchema = z.object({
  id: z.number().int(),
  title: z.string(),
  type: z.enum(['movie', 'series']),
  notes: z.string().nullable(),
  fulfilled: z.boolean(),
  createdAt: DateString,
  updatedAt: DateString,
})

export type Requested = z.infer<typeof RequestedSchema>

// Omit DB-managed fields; PATCH schemas are fully partial.
const DB_META = {
  id: true,
  createdAt: true,
  updatedAt: true,
  outdated: true,
  mismatched: true,
  mismatchCount: true,
  mismatchReported: true,
} as const

export const CreateFilmSchema = FilmSchema.omit(DB_META)
export const UpdateFilmSchema = CreateFilmSchema.omit({ imdbId: true }).partial()

export type CreateFilm = z.infer<typeof CreateFilmSchema>
export type UpdateFilm = z.infer<typeof UpdateFilmSchema>

export const CreateSeriesSchema = SeriesSchema.omit(DB_META)
export const UpdateSeriesSchema = CreateSeriesSchema.omit({ imdbId: true }).partial()

export type CreateSeries = z.infer<typeof CreateSeriesSchema>
export type UpdateSeries = z.infer<typeof UpdateSeriesSchema>

export const MissingTitleSchema = z.object({
  id: z.number().int(),
  title: z.string(),
  kind: z.enum(['film', 'series', 'unknown']),
  createdAt: DateString,
  updatedAt: DateString,
})

export type MissingTitle = Readonly<z.infer<typeof MissingTitleSchema>>

export const CreateRequestedSchema = RequestedSchema.omit({ id: true, createdAt: true, updatedAt: true })
export const UpdateRequestedSchema = CreateRequestedSchema.partial()

export type CreateRequested = z.infer<typeof CreateRequestedSchema>
export type UpdateRequested = z.infer<typeof UpdateRequestedSchema>
